import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, X, BadgeCheck } from 'lucide-react';
import { db, isFirebaseActive } from '../firebase';
import { ref, onValue, set, update } from 'firebase/database';
import { useUserStore } from '../store/userStore';

interface Message {
  id: string;
  sender: 'customer' | 'admin';
  text: string;
  timestamp: number;
}

const WELCOME_MSG: Message = {
  id: 'welcome',
  sender: 'admin',
  text: "👋 Welcome to SensorZone Live Support! Ask us anything about sensors, boards, or custom projects. We're online and ready to help!",
  timestamp: 0,
};

export const QuickChatBubble: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([WELCOME_MSG]);
  const [inputMsg, setInputMsg] = useState('');
  const [sessionId, setSessionId] = useState<string>('');
  const [unreadCount, setUnreadCount] = useState(0);

  const currentUser = useUserStore((state) => state.currentUser);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Initialize or fetch session ID
  useEffect(() => {
    let sId = localStorage.getItem('sensorzone_chat_session_id');
    if (!sId) {
      sId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
      localStorage.setItem('sensorzone_chat_session_id', sId);
    }
    setSessionId(sId);
  }, []);

  // Listen to messages from Firebase or localStorage fallback
  useEffect(() => {
    if (!sessionId) return;

    if (isFirebaseActive && db) {
      const chatRef = ref(db!, `users/${sessionId}/chat`);
      const unsubscribe = onValue(chatRef, (snapshot) => {
        if (snapshot.exists()) {
          const chatData = snapshot.val();
          const msgsObj = chatData.messages || {};
          const msgsList: Message[] = Object.keys(msgsObj).map((key) => ({
            id: key,
            ...msgsObj[key]
          })).sort((a, b) => a.timestamp - b.timestamp);

          // Always keep the welcome message at top
          const hasWelcome = msgsList.some(m => m.id === 'welcome');
          setMessages(hasWelcome ? msgsList : [WELCOME_MSG, ...msgsList]);

          // Mark read when open
          if (isOpen && chatData.unreadCountForCustomer > 0) {
            update(ref(db!, `users/${sessionId}/chat`), { unreadCountForCustomer: 0 });
          } else {
            setUnreadCount(chatData.unreadCountForCustomer || 0);
          }
        } else {
          setMessages([WELCOME_MSG]);
        }
      });
      return () => unsubscribe();
    } else {
      // LocalStorage Mock mode
      const localData = localStorage.getItem(`sensorzone_local_chat_${sessionId}`);
      if (localData) {
        const parsed: Message[] = JSON.parse(localData);
        const hasWelcome = parsed.some(m => m.id === 'welcome');
        setMessages(hasWelcome ? parsed : [WELCOME_MSG, ...parsed]);
      } else {
        setMessages([WELCOME_MSG]);
      }
    }
  }, [sessionId, isOpen]);

  // Clear unread count when chat is opened
  useEffect(() => {
    if (isOpen && sessionId && isFirebaseActive && db && unreadCount > 0) {
      update(ref(db!, `users/${sessionId}/chat`), { unreadCountForCustomer: 0 });
      setUnreadCount(0);
    }
  }, [isOpen, sessionId, unreadCount]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isOpen]);

  const handleSendMessage = async (textToSend: string) => {
    if (!textToSend.trim() || !sessionId) return;

    const messageText = textToSend.trim();
    const timestamp = Date.now();
    const messageId = `msg_${Date.now()}_${Math.random().toString(36).substring(2, 5)}`;

    const newMsg: Message = {
      id: messageId,
      sender: 'customer',
      text: messageText,
      timestamp,
    };

    // Optimistic update — show message immediately
    setMessages((prev) => [...prev, newMsg]);
    setInputMsg('');

    if (isFirebaseActive && db) {
      try {
        await set(ref(db!, `users/${sessionId}/chat/messages/${messageId}`), newMsg);

        const customerName = currentUser ? currentUser.name : 'Guest Customer';
        const customerEmail = currentUser ? currentUser.email : 'guest@sensorzone.lk';

        await update(ref(db!, `users/${sessionId}/chat`), {
          sessionId,
          customerName,
          customerEmail,
          lastMessageAt: timestamp,
          lastMessageText: messageText,
          unreadCountForAdmin: messages.filter(m => m.sender === 'customer').length + 1,
          unreadCountForCustomer: 0,
        });
      } catch (err) {
        console.error('Failed to send live chat message:', err);
      }
    } else {
      // LocalStorage simulated auto-reply mode
      setMessages((prev) => {
        const updated = [...prev, newMsg];
        localStorage.setItem(`sensorzone_local_chat_${sessionId}`, JSON.stringify(updated));
        return updated;
      });

      setTimeout(() => {
        const replyId = `msg_reply_${Date.now()}`;
        const autoReply: Message = {
          id: replyId,
          sender: 'admin',
          text: `Thank you for your message! In offline mode, real-time admin replies are unavailable. Please connect to the internet for live chat.`,
          timestamp: Date.now(),
        };
        setMessages((prev) => {
          const updated = [...prev, autoReply];
          localStorage.setItem(`sensorzone_local_chat_${sessionId}`, JSON.stringify(updated));
          return updated;
        });
      }, 1500);
    }
  };

  const handleSendForm = (e: React.FormEvent) => {
    e.preventDefault();
    handleSendMessage(inputMsg);
  };

  return (
    <div className="fixed bottom-6 right-6 z-40 no-print font-sans flex flex-col items-end gap-3">
      {/* Quick Chat popup — opens to the LEFT of the button */}
      {isOpen && (
        <div className="w-80 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col h-[420px] animate-slide-up">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary to-indigo-700 text-white p-4 flex justify-between items-center shrink-0">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center font-bold text-xs">
                SZ
              </div>
              <div>
                <h4 className="font-extrabold text-xs flex items-center gap-1">
                  SensorZone Live Support
                  <BadgeCheck className="w-3.5 h-3.5 text-accent fill-accent/15" />
                </h4>
                <p className="text-[9px] text-indigo-100 flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  Online Helpdesk
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages Area */}
          <div className="flex-grow p-4 bg-slate-50 overflow-y-auto space-y-3 flex flex-col">
            {messages.map((msg) => {
              const isAdmin = msg.sender === 'admin';
              return (
                <div
                  key={msg.id}
                  className={`flex flex-col max-w-[85%] ${
                    isAdmin ? 'self-start' : 'self-end items-end'
                  }`}
                >
                  <div
                    className={`p-3 rounded-2xl text-xs leading-relaxed shadow-sm border ${
                      isAdmin
                        ? 'bg-white text-slate-700 rounded-tl-none border-slate-100'
                        : 'bg-primary text-white rounded-tr-none border-primary/20'
                    }`}
                  >
                    {msg.text}
                  </div>
                  {msg.id !== 'welcome' && (
                    <span className="text-[8px] text-slate-500 mt-1 font-semibold">
                      {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Message Chips */}
          <div className="px-3 py-2 bg-white border-t border-slate-100 overflow-x-auto shrink-0 scrollbar-none">
            <div className="flex gap-1.5 whitespace-nowrap text-[9px]">
              <button
                type="button"
                onClick={() => handleSendMessage("Do you have stock for ESP32 and Arduino boards?")}
                className="bg-slate-100 hover:bg-slate-200 border border-slate-200/50 text-slate-600 py-1 px-2.5 rounded-full font-bold transition-all"
              >
                📦 Check Stock
              </button>
              <button
                type="button"
                onClick={() => handleSendMessage("How can I track my order?")}
                className="bg-slate-100 hover:bg-slate-200 border border-slate-200/50 text-slate-600 py-1 px-2.5 rounded-full font-bold transition-all"
              >
                🚚 Track Order
              </button>
              <button
                type="button"
                onClick={() => handleSendMessage("I need consultancy for a custom hardware development.")}
                className="bg-slate-100 hover:bg-slate-200 border border-slate-200/50 text-slate-600 py-1 px-2.5 rounded-full font-bold transition-all"
              >
                💡 Custom Project
              </button>
            </div>
          </div>

          {/* Chat input form */}
          <form onSubmit={handleSendForm} className="p-3 bg-white border-t border-slate-100 flex items-center gap-2 shrink-0">
            <input
              type="text"
              placeholder="Type support inquiry..."
              className="input input-sm input-bordered w-full rounded-full focus:outline-none focus:ring-1 focus:ring-primary text-xs"
              value={inputMsg}
              onChange={(e) => setInputMsg(e.target.value)}
            />
            <button
              type="submit"
              className="btn btn-circle w-11 h-11 min-h-0 bg-primary hover:bg-primary/90 text-white border-none flex items-center justify-center shadow-md shadow-indigo-950/15"
            >
              <Send className="w-3.5 h-3.5 text-accent" />
            </button>
          </form>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-12 h-12 bg-gradient-to-tr from-primary to-indigo-600 hover:from-primary/95 hover:to-indigo-700 text-white rounded-full shadow-xl flex items-center justify-center hover:scale-105 transition-transform duration-300 relative group focus:outline-none"
        aria-label="Open Live Chat Support"
      >
        <MessageSquare className="w-5 h-5 text-accent" />

        {/* Pulsing Outer Glow */}
        <span className="absolute -inset-0.5 rounded-full bg-primary/30 -z-10 animate-ping"></span>

        {/* Unread badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-600 text-white text-[8px] font-bold rounded-full w-4 h-4 flex items-center justify-center border-2 border-white animate-bounce">
            {unreadCount}
          </span>
        )}

        {/* Tooltip */}
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-slate-900 text-white text-[10px] font-bold py-1 px-2.5 rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none whitespace-nowrap">
          Live Chat
        </span>
      </button>
    </div>
  );
};
